// src/platform/projectArtifacts.ts

/**
 * Project Artifacts - cross-device read-back of a project's strategic memory.
 *
 * Nuvra writes five strategic artifacts to Supabase during reasoning (blueprint,
 * discovery report, solution concept, implication analysis, recommendation set),
 * each owner-scoped by RLS. They were write-only on the client: never read back,
 * so the product's strategic memory vanished on reload or a device switch.
 *
 * loadProjectArtifacts is the missing read path. It pulls the latest version of
 * each artifact for a project, in parallel, fault-contained (one artifact failing
 * never sinks the rest), so opening a project on any device restores everything
 * Nuvra reasoned, not just the schema.
 */

import { readLatestBlueprint }            from "@/platform/artifactRepository";
import { readLatestDiscoveryReport }      from "@/platform/discoveryRepository";
import { readLatestSolutionConcept }      from "@/platform/solutionConceptRepository";
import { readLatestImplicationAnalysis }  from "@/platform/implicationRepository";
import { readLatestRecommendationSet }    from "@/platform/recommendationRepository";
import type { Blueprint }                 from "@/intelligence/architecturePITypes";
import type {
  DiscoveryReport,
  SolutionConceptStatement,
  StructuredImplicationAnalysis,
  RecommendationSet,
} from "@/intelligence/strategicPITypes";
import { logger } from "@/system/logger";

export interface ProjectArtifactBundle {
  blueprint:      Blueprint | null;
  discovery:      DiscoveryReport | null;
  solutionConcept: SolutionConceptStatement | null;
  implication:    StructuredImplicationAnalysis | null;
  recommendation: RecommendationSet | null;
  /** True when at least one artifact was found for this project. */
  hasAny:         boolean;
}

export const EMPTY_ARTIFACT_BUNDLE: ProjectArtifactBundle = {
  blueprint: null, discovery: null, solutionConcept: null,
  implication: null, recommendation: null, hasAny: false,
};

/** Run a single artifact read, swallowing failure to null so siblings survive. */
async function safeRead<T>(label: string, projectId: string, read: (id: string) => Promise<T | null>): Promise<T | null> {
  try {
    return await read(projectId);
  } catch (err) {
    logger.warn("[projectArtifacts] read failed", { artifact: label, projectId, err: err instanceof Error ? err.message : String(err) });
    return null;
  }
}

/**
 * Load the latest strategic artifacts for a project. Always resolves (never
 * rejects); missing or failed artifacts are null. Returns the empty bundle for a
 * blank projectId.
 */
export async function loadProjectArtifacts(projectId: string): Promise<ProjectArtifactBundle> {
  if (!projectId) return { ...EMPTY_ARTIFACT_BUNDLE };

  const [blueprint, discovery, solutionConcept, implication, recommendation] = await Promise.all([
    safeRead("blueprint",       projectId, readLatestBlueprint),
    safeRead("discovery",       projectId, readLatestDiscoveryReport),
    safeRead("solutionConcept", projectId, readLatestSolutionConcept),
    safeRead("implication",     projectId, readLatestImplicationAnalysis),
    safeRead("recommendation",  projectId, readLatestRecommendationSet),
  ]);

  return {
    blueprint,
    discovery,
    solutionConcept,
    implication,
    recommendation,
    hasAny: !!(blueprint || discovery || solutionConcept || implication || recommendation),
  };
}
